import { useEffect, useRef, useState, useCallback } from "react";
import GuideBody from "@/components/guides/GuideBody";

type Wrap = { before: string; after?: string; placeholder: string };
type LinePrefix = { prefix: string; placeholder: string };

// Each button either wraps the selection (bold, code) or prefixes whole lines
// (headings, lists, quotes). Keeping those two shapes apart is what makes
// multi-line selections behave sensibly.
const WRAPS: Record<string, Wrap> = {
  bold: { before: "**", after: "**", placeholder: "bold text" },
  italic: { before: "_", after: "_", placeholder: "italic text" },
  code: { before: "`", after: "`", placeholder: "code" },
  link: { before: "[", after: "](https://)", placeholder: "link text" },
};

const LINE_PREFIXES: Record<string, LinePrefix> = {
  h2: { prefix: "## ", placeholder: "Heading" },
  h3: { prefix: "### ", placeholder: "Subheading" },
  bullet: { prefix: "- ", placeholder: "List item" },
  number: { prefix: "1. ", placeholder: "List item" },
  quote: { prefix: "> ", placeholder: "Quote" },
};

export default function MarkdownEditor({
  value,
  onChange,
  initialHtml,
}: {
  value: string;
  onChange: (next: string) => void;
  initialHtml?: string;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [html, setHtml] = useState(initialHtml ?? "");
  const [rendering, setRendering] = useState(false);
  // On narrow screens there isn't room for both panes at once.
  const [mobileTab, setMobileTab] = useState<"write" | "preview">("write");

  // Rendered on the server with the same renderer the live page uses, so the
  // preview can't drift from what ships. Debounced so a burst of typing is one
  // request, not one per keystroke.
  useEffect(() => {
    const body = value;
    const timer = setTimeout(async () => {
      setRendering(true);
      try {
        const res = await fetch("/admin/guides/preview", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token":
              document
                .querySelector('meta[name="csrf-token"]')
                ?.getAttribute("content") ?? "",
          },
          body: JSON.stringify({ body }),
        });
        if (res.ok) setHtml((await res.json()).html);
      } catch {
        // A failed preview is not worth interrupting writing over; the last
        // good render stays on screen.
      } finally {
        setRendering(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [value]);

  // Replaces the selection and restores focus with the new text selected, so
  // the author can keep typing over the placeholder.
  const apply = useCallback(
    (next: string, selStart: number, selEnd: number) => {
      onChange(next);
      requestAnimationFrame(() => {
        const el = ref.current;
        if (!el) return;
        el.focus();
        el.setSelectionRange(selStart, selEnd);
      });
    },
    [onChange],
  );

  function wrap(kind: keyof typeof WRAPS) {
    const el = ref.current;
    if (!el) return;

    const { before, after = "", placeholder } = WRAPS[kind];
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const selected = value.slice(start, end) || placeholder;
    const next =
      value.slice(0, start) + before + selected + after + value.slice(end);

    apply(next, start + before.length, start + before.length + selected.length);
  }

  function prefixLines(kind: keyof typeof LINE_PREFIXES) {
    const el = ref.current;
    if (!el) return;

    const { prefix, placeholder } = LINE_PREFIXES[kind];
    const start = el.selectionStart;
    const end = el.selectionEnd;

    // Expand to whole lines so a prefix never lands mid-word.
    const lineStart = value.lastIndexOf("\n", start - 1) + 1;
    const lineEndRaw = value.indexOf("\n", end);
    const lineEnd = lineEndRaw === -1 ? value.length : lineEndRaw;

    const block = value.slice(lineStart, lineEnd) || placeholder;
    const prefixed = block
      .split("\n")
      .map((line) =>
        line.startsWith(prefix) ? line.slice(prefix.length) : prefix + line,
      )
      .join("\n");

    const next = value.slice(0, lineStart) + prefixed + value.slice(lineEnd);
    apply(next, lineStart, lineStart + prefixed.length);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (!(e.metaKey || e.ctrlKey)) return;

    const key = e.key.toLowerCase();
    if (key === "b") {
      e.preventDefault();
      wrap("bold");
    } else if (key === "i") {
      e.preventDefault();
      wrap("italic");
    } else if (key === "k") {
      e.preventDefault();
      wrap("link");
    }
  }

  const tools: { label: string; title: string; run: () => void }[] = [
    { label: "B", title: "Bold (⌘B)", run: () => wrap("bold") },
    { label: "I", title: "Italic (⌘I)", run: () => wrap("italic") },
    { label: "H2", title: "Heading", run: () => prefixLines("h2") },
    { label: "H3", title: "Subheading", run: () => prefixLines("h3") },
    { label: "•", title: "Bullet list", run: () => prefixLines("bullet") },
    { label: "1.", title: "Numbered list", run: () => prefixLines("number") },
    { label: "❝", title: "Quote", run: () => prefixLines("quote") },
    { label: "</>", title: "Code", run: () => wrap("code") },
    { label: "🔗", title: "Link (⌘K)", run: () => wrap("link") },
  ];

  return (
    <div className="flex flex-col gap-3">
      <div className="border-line/30 flex flex-wrap items-center gap-1 border-b pb-2">
        {tools.map((tool) => (
          <button
            key={tool.label}
            type="button"
            title={tool.title}
            onClick={tool.run}
            className="border-line/40 bg-surface text-ink hover:bg-goldenrod duration-fast ease-standard min-w-8 cursor-pointer border px-2 py-1 text-xs font-bold transition-colors"
          >
            {tool.label}
          </button>
        ))}

        <span className="text-caption text-ink-soft ml-auto">
          {rendering ? "rendering…" : `${value.length.toLocaleString()} chars`}
        </span>
      </div>

      {/* Two panes side by side once there's room; tabbed below that. */}
      <div className="flex gap-2 lg:hidden">
        {(["write", "preview"] as const).map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setMobileTab(tab)}
            className={`cursor-pointer border-2 px-3 py-1 text-sm font-bold ${
              mobileTab === tab
                ? "border-line bg-goldenrod text-ink"
                : "border-line/30 text-ink-soft"
            }`}
          >
            {tab === "write" ? "Write" : "Preview"}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <textarea
          ref={ref}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          spellCheck
          rows={26}
          className={`border-line bg-surface-plain text-ink text-caption focus:border-link w-full border-2 p-3 font-mono leading-relaxed outline-none ${
            mobileTab === "write" ? "" : "hidden lg:block"
          }`}
          placeholder="Write in Markdown. Raw HTML is stripped when it renders."
        />

        <div
          className={`border-line/30 bg-surface max-h-[42rem] overflow-y-auto border-2 p-4 ${
            mobileTab === "preview" ? "" : "hidden lg:block"
          }`}
        >
          {html ? (
            <GuideBody html={html} />
          ) : (
            <p className="text-body text-ink-soft">
              The preview appears here as you type, rendered by the same code as
              the live page.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
